import React, { memo, useEffect } from 'react';
import { WellSelectionMouseEvents } from '../constants';
import { RMRectangle } from 'business/models/CustomModels';
import { Group, Rect } from 'react-konva';
import { GetRelativePointerPosition, isCtrlKeyPressed } from 'utils/Global';
import { getWellFromAbsPoint, getWellNumbersBetweenStartAndEnd } from '../utils';
import CreateLayerTools from 'business/plateviewer/layer/CreateLayerTools';

interface Props {
  canvasRefs: { stageRef: any; wellSelectionLayer: any };
  containerRows: number;
  containerCols: number;
  wellRects: RMRectangle[];
  selectedWellCoordinates: RMRectangle[];
  setWellSelectionMouseEvents: React.Dispatch<
    React.SetStateAction<WellSelectionMouseEvents>
  >;
  canvasRectangleSize: number;
}

let startWell: { x: number; y: number } = null;
let endWell: { x: number; y: number } = null;
let layerTools = null;
let wellSelection = false;
let isControlKeyPressed = false;

//TODO: Handle multi-plate selection
const WellSelection = (props: Props) => {
  const {
    canvasRefs,
    containerRows,
    containerCols,
    wellRects,
    selectedWellCoordinates,
    setWellSelectionMouseEvents,
    canvasRectangleSize,
  } = props;
  const { stageRef, wellSelectionLayer: drawingLayerRef } = canvasRefs;

  const onMouseDown = (evt) => {
    if (evt.evt.button == 0) {
      const pointerPosition = GetRelativePointerPosition(stageRef);
      startWell = getWellFromAbsPoint(
        containerRows,
        containerCols,
        pointerPosition,
        wellRects,
        canvasRectangleSize,
      );
      endWell = startWell;
      layerTools = new CreateLayerTools(drawingLayerRef);
      layerTools.onMouseDown(evt);
      wellSelection = true;
    }
  };

  const onMouseMove = (evt) => {
    if (wellSelection && !evt.evt.altKey) {
      const pointerPosition = GetRelativePointerPosition(stageRef);
      endWell = getWellFromAbsPoint(
        containerRows,
        containerCols,
        pointerPosition,
        wellRects,
        canvasRectangleSize,
        30,
      );
      const startWellNum = startWell.y * containerCols + startWell.x;
      const endWellNum = endWell.y * containerCols + endWell.x;
      if (wellRects[startWellNum] && wellRects[endWellNum]) {
        layerTools.onMouseMove(evt, wellRects[startWellNum], wellRects[endWellNum]);
      }
    }
  };

  const getWellSelectionDetails = (evt) => {
    if (evt.evt.button == 1) {
      stageRef.draggable(false);
    } else if (wellSelection) {
      wellSelection = false;

      const selectedWellNumbers = getWellNumbersBetweenStartAndEnd(
        startWell,
        endWell,
        containerRows,
        containerCols,
      );
      if (drawingLayerRef) {
        drawingLayerRef.destroyChildren();
        drawingLayerRef.clear();
        drawingLayerRef.batchDraw();
      }
      return { selectedWellNumbers, appendSelection: isControlKeyPressed };
    }
  };

  const handleUserKeyPress = (e) => {
    if (isCtrlKeyPressed(e) == true) {
      isControlKeyPressed = true;
    } else {
      isControlKeyPressed = false;
    }
  };

  useEffect(() => {
    'keyup keydown'.split(' ').forEach(function (e) {
      window.addEventListener(e, handleUserKeyPress);
    });

    return () => {
      'keyup keydown'.split(' ').forEach(function (e) {
        window.removeEventListener(e, handleUserKeyPress);
      });
    };
  }, []);

  useEffect(() => {
    setWellSelectionMouseEvents({
      onMouseMove,
      onMouseDown,
      getWellSelectionDetails,
    });
  }, [canvasRefs, containerRows, containerCols, wellRects, isControlKeyPressed]);

  const getSelectedWell = () =>
    selectedWellCoordinates.length === 0
      ? null
      : selectedWellCoordinates.map((coord, index) => {
          if (!coord) {
            return null;
          }
          const { x, y, w, h } = coord;
          return (
            <Rect
              key={`wellSelectionRect-${index}`}
              id={`wellselection-selected-rect-${index}`}
              name="wellSelectionRect"
              x={x}
              y={y}
              width={w}
              height={h}
              stroke="#F7A246"
              strokeWidth={3}
              shadowBlur={0}
              listening={false}
            />
          );
        });

  return (
    <Group id={`wellselection-selected-group`} listening={false}>
      {getSelectedWell()}
    </Group>
  );
};

export default memo(WellSelection);
